import { ATSPrincipalAppCandidateModalTabsTestIds } from 'data-testids/ATS';
import {
  BasicInfoBox,
  GreyText,
  NoneFoundText,
  SubHeader,
  Subtitle,
} from 'features/Principal/PrincipalCandidatesList/styles';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { CredentialVerificationStatus } from 'utils/typedEnums';
import { getMissouriCredentialExamsText } from './utils';
import { IndividualCredentialProps } from 'features/Principal/types/candidatesListTypes';
import { USStates } from 'utils/enums';
import moment from 'moment';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { CredentialsProps } from 'features/Principal/types/modalTypes';

const stateToDepartmentName = {
  MO: 'Missouri Department of Elementary and Secondary Education',
  TN: 'Tennessee Department of Education',
};

export const IndividualCredential: React.FC<IndividualCredentialProps> = ({ credential }) => {
  const theme = useTheme();

  const credentialState =
    credential?.state && USStates()?.find((state) => state.id === credential.state).label;
  const credentialExamsPassedText = getMissouriCredentialExamsText(credential);

  const isVerified =
    credential.verification_status === CredentialVerificationStatus.PASSED_AUTO_VERIFICATION ||
    credential.verification_status === CredentialVerificationStatus.PASSED_MANUAL_VERIFICATION;

  const getVerifiedCopy = () => {
    const verifiedFrom =
      stateToDepartmentName[credentialState as keyof typeof stateToDepartmentName];
    const verifiedOn = moment(credential.verified_on).format('MM/DD/YYYY [at] hh:mm a');

    if (!verifiedFrom) {
      return `Verified on ${verifiedOn}`;
    }

    return `Verified from ${verifiedFrom} on ${verifiedOn}`;
  };

  return (
    <BasicInfoBox
      sx={{ flexDirection: 'row' }}
      data-testid={ATSPrincipalAppCandidateModalTabsTestIds.INDIVIDUAL_CREDENTIAL}
    >
      <Stack direction="row" spacing={2}>
        {isVerified && <VerifiedUserOutlinedIcon color="primary" sx={{ flexShrink: 0 }} />}
        <Stack direction="column" sx={{ width: '100%' }}>
          <Stack justifyContent="space-between" sx={{ width: '100%', marginTop: '-10px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row">
                {credential.state && <Subtitle>{credentialState}</Subtitle>}{' '}
                {credential.subject && <Subtitle>- {credential.subject.label} </Subtitle>}
                {credential.grade && <Subtitle>- {credential.grade.label} </Subtitle>}
              </Stack>
              {credential.status && (
                <Chip
                  sx={{ backgroundColor: theme.palette.white.darker }}
                  label={credential.status.label}
                  data-testid={ATSPrincipalAppCandidateModalTabsTestIds.VERIFIED_CHIP}
                />
              )}
            </Stack>
            {credential.expiration_date && (
              <Subtitle>
                Expires: {new Date(credential.expiration_date).toLocaleDateString('en-US')}
              </Subtitle>
            )}
            <Box sx={{ color: theme.palette.text.disabled, pt: credential.license_number ? 2 : 0 }}>
              {credential.license_number && (
                <Typography>License #: {credential.license_number}</Typography>
              )}
              {credential.issue_date && (
                <Typography>
                  Issued: {new Date(credential.issue_date).toLocaleDateString('en-US')}
                </Typography>
              )}
            </Box>
            {!credential.state && (
              <Box sx={{ color: theme.palette.text.disabled }}>
                <Subtitle>Other Credential</Subtitle>
                <GreyText>{credential.credential_explanation}</GreyText>
              </Box>
            )}
            {credential?.credential_exams_passed?.length > 0 && (
              <Typography sx={{ color: theme.palette.text.disabled }}>
                {credentialExamsPassedText}
              </Typography>
            )}
          </Stack>

          {isVerified && (
            <GreyText sx={{ mt: 1, fontStyle: 'italic' }}>{getVerifiedCopy()}</GreyText>
          )}
        </Stack>
      </Stack>
    </BasicInfoBox>
  );
};

export const Credentials: React.FC<CredentialsProps> = ({ credentials }) => {
  return (
    <Box data-testid={ATSPrincipalAppCandidateModalTabsTestIds.CREDENTIALS_SECTION}>
      <SubHeader variant="h3" sx={{ mb: 2 }}>
        Credentials
      </SubHeader>
      {credentials.length == 0 && <NoneFoundText>No credentials found</NoneFoundText>}
      {credentials.map((credential) => (
        <IndividualCredential key={credential.id} credential={credential} />
      ))}
    </Box>
  );
};
